/**
 * useRestaurantSearch Hook
 * Kombinierter Hook für Restaurant-Suche mit optimierter Performance
 * 
 * Features:
 * - TanStack Query Integration
 * - Debounced Search
 * - Client-side Filtering & Sorting
 * - Pagination/Infinite Scroll
 * - Selektive Store-Subscriptions (useShallow)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useFilterStore } from '@/lib/FilterStore';
import { queryKeys } from '@/lib/queryKeys';
import { isRestaurantOpenNow } from '@/utils/restaurantTimes';
import { haversineKm, isValidCoord } from '@/lib/distance';
import { useCuisines } from '@/hooks/useCuisines';
import { useSkills } from '@/hooks/useSkills';
import type { 
  RestaurantWithDetails, 
  Cuisine, 
  SortOption, 
  ViewMode,
  CardDensity,
  UseRestaurantSearchReturn 
} from '@/types/search';

// ============================================
// DEBOUNCE HOOK
// ============================================

function useDebouncedValue<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ============================================
// MAIN HOOK
// ============================================

interface UseRestaurantSearchOptions {
  viewMode: ViewMode | 'map';
  cardDensity: CardDensity;
}

export function useRestaurantSearch(options: UseRestaurantSearchOptions): UseRestaurantSearchReturn {
  const { viewMode, cardDensity } = options;
  
  // Selektive Store-Subscriptions (Performance: nur relevante Felder)
  const { 
    query,
    location,
    cuisineIds,
    skills: selectedSkills,
    distance,
    openNow,
    deliveryOnly,
    deliveryMode,
    priceLevel,
    sortOption,
    resetAll,
    getActiveFiltersCount
  } = useFilterStore(useShallow(state => ({
    query: state.query,
    location: state.location,
    cuisineIds: state.cuisineIds,
    skills: state.skills,
    distance: state.distance,
    openNow: state.openNow,
    deliveryOnly: state.deliveryOnly,
    deliveryMode: state.deliveryMode,
    priceLevel: state.priceLevel,
    sortOption: state.sortOption,
    resetAll: state.resetAll,
    getActiveFiltersCount: state.getActiveFiltersCount
  })));

  const sortBy = (sortOption || 'relevance') as SortOption;
  const activeFilterCount = getActiveFiltersCount();
  
  // Debounced Search Query
  const debouncedQuery = useDebouncedValue((query || '').trim(), 250);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = viewMode === 'list'
    ? (cardDensity === 'compact' ? 16 : 12)
    : 9;
  
  // ============================================
  // QUERY KEY GENERATION
  // ============================================
  
  const cuisineKey = useMemo(
    () => [...cuisineIds].sort((a, b) => a - b).join(','),
    [cuisineIds]
  );
  
  const skillKey = useMemo(
    () => [...selectedSkills].sort((a, b) => a - b).join(','),
    [selectedSkills]
  );
  
  const locationKey = useMemo(() => {
    if (typeof location?.lat === 'number' && typeof location?.lng === 'number') {
      return `geo:${location.lat.toFixed(5)},${location.lng.toFixed(5)}`;
    }
    return location?.label ? `label:${location.label.trim().toLowerCase()}` : 'none';
  }, [location?.lat, location?.lng, location?.label]);

  const searchKey = useMemo(() => {
    return [
      `q:${debouncedQuery.toLowerCase()}`,
      `loc:${locationKey}`,
      `dist:${distance}`,
      `cuisines:${cuisineKey}`,
      `skills:${skillKey}`,
      `open:${openNow ? 1 : 0}`,
      `delivery:${deliveryOnly ? 1 : 0}`,
      `mode:${deliveryMode}`,
      `price:${priceLevel || 'all'}`
    ].join('|');
  }, [debouncedQuery, locationKey, distance, cuisineKey, skillKey, openNow, deliveryOnly, deliveryMode, priceLevel]);

  // Reset page on filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchKey, sortBy]);

  // ============================================
  // API QUERY BUILDER
  // ============================================
  
  const buildApiQuery = useCallback(() => {
    const params = new URLSearchParams();
    
    if (debouncedQuery) params.append('q', debouncedQuery);
    if (location?.label) params.append('location', location.label);
    if (cuisineIds.length > 0) params.append('categories', cuisineIds.join(','));
    params.append('distance', distance.toString());
    
    if (typeof location?.lat === 'number' && !isNaN(location.lat)) {
      params.append('lat', location.lat.toString());
    }
    if (typeof location?.lng === 'number' && !isNaN(location.lng)) {
      params.append('lng', location.lng.toString());
    }
    
    if (openNow) params.append('openNow', 'true');
    if (deliveryOnly) params.append('deliveryOnly', 'true');
    // deliveryMode: 'delivery' oder 'pickup' - Backend kann dies für Filterung nutzen
    params.append('deliveryMode', deliveryMode);
    if (selectedSkills.length > 0) {
      params.append('skillIds', selectedSkills.join(','));
    }
    
    return `/api/restaurants/search?${params.toString()}`;
  }, [debouncedQuery, location, cuisineIds, distance, selectedSkills, openNow, deliveryOnly, deliveryMode]);

  // ============================================
  // DATA FETCHING
  // ============================================
  
  const { 
    data: restaurants = [], 
    isLoading: isLoadingRestaurants, 
    isFetching, 
    isError, 
    refetch 
  } = useQuery<RestaurantWithDetails[]>({
    queryKey: queryKeys.restaurants.searchKey(searchKey),
    queryFn: async () => {
      const apiUrl = buildApiQuery();
      const response = await fetch(apiUrl, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Fehler beim Laden der Restaurants (${response.status})`);
      }
      return response.json();
    },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false
  });

  // Cuisines & Skills with aggressive caching
  const { data: cuisines = [], isLoading: isLoadingCuisines } = useCuisines();
  const { data: skills = [], isLoading: isLoadingSkills } = useSkills();

  // ============================================
  // DERIVED CUISINES (fallback from restaurants)
  // ============================================
  
  const derivedCuisines = useMemo(() => {
    const map = new Map<number, Cuisine>();
    restaurants.forEach((restaurant) => {
      (restaurant.categories || []).forEach((category) => {
        if (!category?.id || !category?.name) return;
        const sortOrder = category.sort_order ?? category.sortOrder ?? category.order ?? 0;
        if (!map.has(category.id)) {
          map.set(category.id, {
            id: category.id,
            name: category.name,
            order: sortOrder
          });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => {
      const orderDiff = (a.order || 0) - (b.order || 0);
      return orderDiff !== 0 ? orderDiff : a.name.localeCompare(b.name);
    });
  }, [restaurants]);

  const cuisineOptions = cuisines.length > 0 ? cuisines : derivedCuisines;

  // Dietary Skills Filter
  const dietarySkills = useMemo(() => {
    const dietaryKeywords = [
      'vegan', 'vegetar', 'vegetarian', 'veggie', 'gluten', 
      'laktose', 'lactose', 'milchfrei', 'bio', 'halal', 
      'koscher', 'keto', 'paleo', 'low carb', 'dairy'
    ];
    return skills.filter((skill) => {
      const name = (skill.name || '').toLowerCase();
      return dietaryKeywords.some((keyword) => name.includes(keyword));
    });
  }, [skills]);

  // ============================================
  // DISTANCE CALCULATION
  // ============================================
  
  const hasUserLocation = isValidCoord(location?.lat, location?.lng);

  const restaurantsWithDistance = useMemo(() => {
    if (!hasUserLocation) return restaurants;

    return restaurants.map((restaurant) => {
      const rawLat = restaurant.latitude;
      const rawLng = restaurant.longitude;
      const lat = typeof rawLat === 'number' ? rawLat : Number.parseFloat(rawLat as string);
      const lng = typeof rawLng === 'number' ? rawLng : Number.parseFloat(rawLng as string);
      
      if (isValidCoord(lat, lng) && isValidCoord(location?.lat, location?.lng)) {
        const distanceKm = haversineKm(location!.lat!, location!.lng!, lat, lng);
        return { ...restaurant, distanceKm } as RestaurantWithDetails & { distanceKm: number };
      }
      return restaurant;
    });
  }, [restaurants, hasUserLocation, location?.lat, location?.lng]);

  // ============================================
  // FILTERING PIPELINE
  // ============================================
  
  const filteredRestaurants = useMemo(() => {
    const list = [...restaurantsWithDistance];
    const radiusKm = distance;

    return list.filter((restaurant) => {
      const r = restaurant as any;

      // Text Search Filter (name, city, categories)
      if (debouncedQuery) {
        const q = debouncedQuery.toLowerCase();
        const nameMatch = r.name?.toLowerCase().includes(q);
        const cityMatch = r.city?.toLowerCase().includes(q);
        const categoryMatch = (r.categories || []).some((c: any) => c.name?.toLowerCase().includes(q));
        if (!nameMatch && !cityMatch && !categoryMatch) return false;
      }

      // Open Now Filter
      if (openNow) {
        const { isOpen } = isRestaurantOpenNow(r.openingHours || []);
        if (!isOpen) return false;
      }

      // Delivery Filter (legacy toggle)
      if (deliveryOnly) {
        const hasDelivery = !!(r.has_delivery || r.delivery);
        if (!hasDelivery) return false;
      }

      // DeliveryMode Filter (primary mode: 'delivery' oder 'pickup')
      // - 'delivery': Nur Restaurants die liefern (has_delivery)
      // - 'pickup': Alle Restaurants (jeder kann abholen)
      if (deliveryMode === 'delivery') {
        const hasDelivery = !!(r.has_delivery || r.delivery);
        if (!hasDelivery) return false;
      }
      // Bei 'pickup' zeigen wir alle Restaurants - kein Filter nötig

      // Cuisine Filter
      if (cuisineIds.length > 0) {
        const categoryIds = (r.categories || []).map((c: any) => c.id);
        const hasCuisine = cuisineIds.some((id) => categoryIds.includes(id));
        if (!hasCuisine) return false;
      }

      // Skills Filter
      if (selectedSkills.length > 0) {
        const skillIds = (r.skills || []).map((s: any) => s.id);
        const hasSkill = selectedSkills.some((id) => skillIds.includes(id));
        if (!hasSkill) return false;
      }

      // Price Level Filter (€, €€, €€€, €€€€)
      if (priceLevel) {
        const restaurantPriceLevel = r.priceRange || r.price_range || r.priceLevel || r.price_level;
        const filterLevel = priceLevel.length; // €=1, €€=2, €€€=3, €€€€=4
        if (typeof restaurantPriceLevel === 'number' && restaurantPriceLevel !== filterLevel) {
          return false;
        }
      }

      // Distance Filter (only with location)
      if (hasUserLocation) {
        if (typeof r.distanceKm !== 'number') return false;
        if (r.distanceKm > radiusKm) return false;
      }

      return true;
    });
  }, [
    restaurantsWithDistance, 
    debouncedQuery,
    openNow, 
    deliveryOnly, 
    deliveryMode,
    cuisineIds, 
    selectedSkills,
    priceLevel,
    distance, 
    hasUserLocation
  ]);

  // ============================================
  // SORTING
  // ============================================
  
  const sortedRestaurants = useMemo(() => {
    const list = [...filteredRestaurants];
    
    // Helper: Check if restaurant is currently open
    const getIsOpen = (r: any): boolean => {
      const { isOpen } = isRestaurantOpenNow(r.openingHours || []);
      return isOpen;
    };
    
    list.sort((a, b) => {
      // Priority: Open restaurants first (unless "openNow" filter is active, then all are open)
      if (!openNow) {
        const aOpen = getIsOpen(a);
        const bOpen = getIsOpen(b);
        if (aOpen && !bOpen) return -1;
        if (!aOpen && bOpen) return 1;
      }
      
      // Secondary sort by selected criteria
      switch (sortBy) {
        case 'distance': {
          const aRaw = (a as any).distanceKm ?? (a as any).distance;
          const bRaw = (b as any).distanceKm ?? (b as any).distance;
          const aDist = typeof aRaw === 'number' ? aRaw : Number.parseFloat(aRaw);
          const bDist = typeof bRaw === 'number' ? bRaw : Number.parseFloat(bRaw);
          const aValid = Number.isFinite(aDist);
          const bValid = Number.isFinite(bDist);
          if (aValid && bValid) return aDist - bDist;
          if (aValid) return -1;
          if (bValid) return 1;
          return (b.rating || 0) - (a.rating || 0);
        }
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'name':
          return a.name.localeCompare(b.name);
        default: // relevance
          if (hasUserLocation) {
            const aRaw = (a as any).distanceKm ?? (a as any).distance;
            const bRaw = (b as any).distanceKm ?? (b as any).distance;
            const aDist = typeof aRaw === 'number' ? aRaw : Number.parseFloat(aRaw);
            const bDist = typeof bRaw === 'number' ? bRaw : Number.parseFloat(bRaw);
            const aValid = Number.isFinite(aDist);
            const bValid = Number.isFinite(bDist);
            if (aValid && bValid && aDist !== bDist) return aDist - bDist;
            if (aValid) return -1;
            if (bValid) return 1;
          }
          return (b.rating || 0) - (a.rating || 0);
      }
    });
    
    return list;
  }, [filteredRestaurants, sortBy, hasUserLocation, openNow]);

  // ============================================
  // PAGINATION
  // ============================================
  
  const totalPages = Math.ceil(sortedRestaurants.length / itemsPerPage);
  const useLoadMore = viewMode === 'list';
  const startIndex = useLoadMore ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = useLoadMore ? currentPage * itemsPerPage : startIndex + itemsPerPage;
  
  const currentRestaurants = useMemo(() => {
    return sortedRestaurants.slice(startIndex, endIndex);
  }, [sortedRestaurants, startIndex, endIndex]);
  
  const canLoadMore = useLoadMore && currentPage < totalPages;

  // ============================================
  // ACTIONS
  // ============================================
  
  const handleResetFilters = useCallback(() => {
    resetAll();
    setCurrentPage(1);
  }, [resetAll]);

  // ============================================
  // RETURN
  // ============================================
  
  return {
    // Data
    restaurants: restaurantsWithDistance,
    filteredRestaurants,
    sortedRestaurants,
    cuisines: cuisineOptions,
    skills,
    dietarySkills,
    
    // Pagination
    currentRestaurants,
    currentPage,
    totalPages,
    itemsPerPage,
    setCurrentPage,
    canLoadMore,
    
    // State
    isLoading: isLoadingRestaurants,
    isFetching,
    isError,
    isLoadingCuisines,
    isLoadingSkills,
    hasUserLocation,
    
    // Actions
    refetch,
    handleResetFilters,
    
    // From store
    sortBy,
    activeFilterCount
  };
}
